import {
  FaBolt,
  FaBookOpen,
  FaBuilding,
  FaBullhorn,
  FaCalculator,
  FaCalendarDays,
  FaCamera,
  FaChartLine,
  FaCircleInfo,
  FaCirclePlay,
  FaCircleQuestion,
  FaCircleUser,
  FaClipboardList,
  FaCloudArrowUp,
  FaCompassDrafting,
  FaCreditCard,
  FaCubes,
  FaDesktop,
  FaDownload,
  FaFaucet,
  FaFileExcel,
  FaFileInvoiceDollar,
  FaFilePdf,
  FaFolderOpen,
  FaGear,
  FaHeadset,
  FaHelmetSafety,
  FaKeyboard,
  FaLayerGroup,
  FaLightbulb,
  FaMobileScreen,
  FaPenRuler,
  FaPlug,
  FaPrint,
  FaRocket,
  FaRulerCombined,
  FaScrewdriverWrench,
  FaShareNodes,
  FaShieldHalved,
  FaTable,
  FaTriangleExclamation,
  FaUsers,
  FaWandMagicSparkles,
  FaWonSign,
  type IconType,
} from '@platform/icon';

/**
 * 섹션 아이콘 레지스트리 — **키 ↔ 컴포넌트의 단일 출처**.
 *
 * DB 에는 이 키(문자열)만 저장한다. SVG 마크업을 저장하면 DB 값이 그대로 DOM 에
 * 들어가는 XSS 경로가 되고, 다크모드·크기 대응도 아이콘마다 따로 하게 된다.
 * 키만 두면 색은 `currentColor`, 크기는 CSS 가 알아서 맞춘다.
 *
 * 키는 react-icons 이름이 아니라 **의미 이름**이다. 나중에 아이콘 세트를 바꿔도
 * 저장된 값이 그대로 살아 있어야 하기 때문이다.
 * 아이콘을 추가하려면 `packages/icon/src/icons.ts` 에 먼저 re-export 한다.
 */
export const DOCS_SECTION_ICON = {
  rocket: { icon: FaRocket, label: '시작하기' },
  book: { icon: FaBookOpen, label: '가이드' },
  calculator: { icon: FaCalculator, label: '적산' },
  ruler: { icon: FaRulerCombined, label: '수량 산출' },
  drafting: { icon: FaCompassDrafting, label: '도면' },
  cubes: { icon: FaCubes, label: '구조' },
  finish: { icon: FaPenRuler, label: '마감' },
  electric: { icon: FaBolt, label: '전기' },
  plumbing: { icon: FaFaucet, label: '설비' },
  invoice: { icon: FaFileInvoiceDollar, label: '내역·견적' },
  table: { icon: FaTable, label: '표·데이터' },
  card: { icon: FaCreditCard, label: '결제·구독' },
  users: { icon: FaUsers, label: '조직·멤버' },
  building: { icon: FaBuilding, label: '회사' },
  gear: { icon: FaGear, label: '설정' },
  tools: { icon: FaScrewdriverWrench, label: '도구' },
  download: { icon: FaDownload, label: '설치·다운로드' },
  report: { icon: FaChartLine, label: '리포트' },
  worklog: { icon: FaClipboardList, label: '작업일지' },
  question: { icon: FaCircleQuestion, label: '자주 묻는 질문' },
  info: { icon: FaCircleInfo, label: '안내' },
  layers: { icon: FaLayerGroup, label: '기타' },
  bulb: { icon: FaLightbulb, label: '팁' },
  // 도움말 주제 — 실무 도움말 센터 22곳의 첫 화면 분류에 나온 횟수 순 (2026-10 조사, 같으면 사용 흐름 순).
  // 0곳인 출력·단축키 등은 적산 프로그램 특성상 남겨 맨 뒤에 둔다.
  integration: { icon: FaPlug, label: '연동·자동화' },
  account: { icon: FaCircleUser, label: '계정·로그인' },
  video: { icon: FaCirclePlay, label: '동영상 강좌' },
  ai: { icon: FaWandMagicSparkles, label: 'AI' },
  security: { icon: FaShieldHalved, label: '보안·권한' },
  trouble: { icon: FaTriangleExclamation, label: '문제 해결' },
  news: { icon: FaBullhorn, label: '새 소식' },
  support: { icon: FaHeadset, label: '고객지원' },
  pc: { icon: FaDesktop, label: 'PC 프로그램' },
  mobile: { icon: FaMobileScreen, label: '모바일 앱' },
  share: { icon: FaShareNodes, label: '공유' },
  cloud: { icon: FaCloudArrowUp, label: '클라우드·동기화' },
  project: { icon: FaFolderOpen, label: '프로젝트·파일' },
  price: { icon: FaWonSign, label: '단가·자원' },
  excel: { icon: FaFileExcel, label: '엑셀 가져오기·내보내기' },
  schedule: { icon: FaCalendarDays, label: '일정·공정' },
  photo: { icon: FaCamera, label: '현장 사진' },
  pdf: { icon: FaFilePdf, label: 'PDF' },
  print: { icon: FaPrint, label: '출력' },
  shortcut: { icon: FaKeyboard, label: '단축키' },
  safety: { icon: FaHelmetSafety, label: '현장·안전' },
} as const satisfies Record<string, { icon: IconType; label: string }>;

export type DocsSectionIconKey = keyof typeof DOCS_SECTION_ICON;

/** 레지스트리에 없는 키(옛 데이터·배포 시차)는 여기로 떨어진다. */
export const DEFAULT_SECTION_ICON: DocsSectionIconKey = 'layers';

export function resolveSectionIcon(key: string | null | undefined): IconType {
  if (key && key in DOCS_SECTION_ICON) {
    return DOCS_SECTION_ICON[key as DocsSectionIconKey].icon;
  }
  return DOCS_SECTION_ICON[DEFAULT_SECTION_ICON].icon;
}

export const DOCS_SECTION_ICON_KEYS = Object.keys(
  DOCS_SECTION_ICON,
) as DocsSectionIconKey[];
